import { cn } from "@/lib/utils";
import { useLearnerStreak } from "@/hooks/useLearnerStreak";

// Vendor mascot, downscaled from the prototype's 1254px/788KB original to
// 160px/26KB — it never renders larger than 56px.
const STREAK_MASCOT = "/images/streak.png";

/**
 * Sidebar activity-streak widget (learner only).
 *
 * Mirrors the vendor design's two states: a bordered card when the sidebar is
 * expanded, and an icon + two-line caption when collapsed.
 *
 * Renders nothing until there is a streak to show — a brand-new learner, an
 * admin, or a failed request all produce no widget rather than "0 Days".
 */
export const StreakCard = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { data } = useLearnerStreak();
  const streak = data?.data;

  if (!streak || streak.current_streak < 1) return null;

  const days = streak.current_streak;
  const label = `${days} ${days === 1 ? "Day" : "Days"} Streak!`;
  const range = formatRange(streak.start_date, streak.end_date);

  // Dimmed while the streak is alive but today isn't logged yet — the learner
  // still has the rest of the day to keep it.
  const atRisk = !streak.active_today;

  if (isCollapsed) {
    return (
      <div
        className="flex flex-col items-center gap-1 text-center"
        title={range ? `${label} (${range})` : label}
      >
        <img
          src={STREAK_MASCOT}
          alt=""
          width={40}
          height={40}
          className={cn("h-10 w-10 object-contain", atRisk && "opacity-40 grayscale")}
        />
        <p className="text-xs font-semibold leading-tight text-zinc-900">
          {days} {days === 1 ? "Day" : "Days"}
          <br />
          Streak!
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-100 bg-white p-4 text-center shadow-sm">
      <div
        className={cn(
          "mx-auto flex h-16 w-16 items-center justify-center rounded-2xl",
          atRisk ? "bg-zinc-100" : "bg-cyan-50",
        )}
      >
        <img
          src={STREAK_MASCOT}
          alt=""
          width={56}
          height={56}
          className={cn("h-14 w-14 object-contain", atRisk && "opacity-40 grayscale")}
        />
      </div>
      <p className="mt-3 text-sm font-semibold text-zinc-900">{label}</p>
      {range && <p className="text-xs text-zinc-500">{range}</p>}
      {atRisk && (
        <p className="mt-1 text-[11px] text-amber-600">Learn today to keep it</p>
      )}
    </div>
  );
};

/** "2026-08-04", "2026-08-07" -> "04 AUG - 07 AUG" (vendor's caption format). */
function formatRange(start: string | null, end: string | null): string | null {
  if (!start || !end) return null;

  const fmt = (iso: string) => {
    // Parse the plain date parts directly — `new Date('2026-08-04')` is UTC
    // midnight, which renders as the previous day west of GMT.
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d);
    if (Number.isNaN(date.getTime())) return null;
    return `${String(d).padStart(2, "0")} ${date
      .toLocaleString("en-GB", { month: "short" })
      .toUpperCase()}`;
  };

  const from = fmt(start);
  const to = fmt(end);
  if (!from || !to) return null;

  return from === to ? from : `${from} - ${to}`;
}
